/**
 * /reset-password — the landing page for a password-recovery link.
 *
 * Mechanically identical to accepting an invite: the SDK exchanges the tokens
 * in the URL for a session, then the person sets a password.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../auth/useAuth.js';
import Field from '../components/Field.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';

export function ResetPasswordPage() {
  const { authStatus } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (authStatus === 'loading' || (authStatus === 'signed-out' && !settled)) {
    return (
      <div className="centered-page" aria-busy="true">
        <p className="muted">Checking your reset link…</p>
      </div>
    );
  }

  if (authStatus === 'signed-out') {
    return (
      <div className="centered-page">
        <div className="card">
          <h1>This reset link didn’t work</h1>
          <p>Reset links can only be used once and expire after an hour.</p>
          <button type="button" className="btn btn--primary" onClick={() => navigate('/forgot-password')}>
            Request a new link
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);

    if (password.length < 10) {
      setError('Please use at least 10 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      setBusy(false);
      return;
    }
    navigate('/', { replace: true });
  }

  return (
    <div className="centered-page">
      <form className="card" onSubmit={handleSubmit}>
        <h1>Choose a new password</h1>

        <ErrorBanner error={error} />

        <Field
          label="New password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          hint="At least 10 characters."
          disabled={busy}
        />
        <Field
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          disabled={busy}
        />

        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save new password'}
        </button>
      </form>
    </div>
  );
}

export default ResetPasswordPage;
